import type { estypes } from '@elastic/elasticsearch';

import { type PlayerDataResult, type PlayerDataSearchResponseBody } from '~/search-domain/pipelines/player-data/types';
import { type SearchDomainHitWithSource, type SearchPipelineEnrichments } from '~/search-domain/types';
import { replaceResponseHits } from '~/search-domain/utils';

function toTotalValue(total: estypes.SearchHitsMetadata<unknown>['total'], fallback: number): number {
  if (!total) return fallback;
  if (typeof total === 'number') return total;
  return total.value;
}

function mapHitWithBookmakerCount(
  hit: estypes.SearchHit<PlayerDataResult>,
  bookmakerCountsByBetId: Map<string, number>,
): estypes.SearchHit<PlayerDataResult> {
  const source = (hit as SearchDomainHitWithSource<PlayerDataResult>)._source;
  if (!source) return hit;

  const bookmakersCount = bookmakerCountsByBetId.get(String(source.betId));
  if (bookmakersCount === undefined) return hit;

  return {
    ...hit,
    _source: {
      ...source,
      bookmakersCount,
    },
  };
}

export function createResponseFactory(
  searchResponse: PlayerDataSearchResponseBody,
  bookmakerCountsByBetId?: SearchPipelineEnrichments['bookmakerCountsByBetId'],
): PlayerDataSearchResponseBody {
  if (!bookmakerCountsByBetId?.size) {
    return searchResponse;
  }

  const hits = searchResponse.hits?.hits ?? [];
  if (!hits.length) {
    return searchResponse;
  }

  const enrichedHits = hits.map(hit => mapHitWithBookmakerCount(hit, bookmakerCountsByBetId));

  return replaceResponseHits(searchResponse, {
    hits: enrichedHits,
    totalValue: toTotalValue(searchResponse.hits?.total, hits.length),
  });
}
